import { Empty, Input, InputRef, Spin } from "antd-v5";
import classNames from "classnames";
import { debounce } from "lodash";
import {
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CancellableAPIRequest } from "restAPI";

import "./index.less";
import {
  getNextSelectableIndex,
  getPreviousSelectableIndex,
} from "./selectableIndexUtils";

type OptionState = {
  selected: boolean;
  highlighted: boolean;
};

export type ACAsyncSearchListState<TItem> = {
  query: string;
  loading: boolean;
  errorText: string;
  results: TItem[];
  reload: () => Promise<void>;
  clearQuery: () => Promise<void>;
};

export type EmptyTextContent = (state: {
  query: string;
  loading: boolean;
  errorText: string;
}) => string;

export type ErrorTextContent = (
  error: string,
  state: { query: string },
) => string;

export type ACAsyncSearchListProps<TItem> = {
  fetchOptions: (query: string) => CancellableAPIRequest<TItem[]>;
  getOptionKey: (item: TItem) => string | number;
  renderOption: (item: TItem, state: OptionState) => ReactNode;
  onSelect: (item: TItem) => void;
  isSelected?: (item: TItem) => boolean;
  isOptionDisabled?: (item: TItem) => boolean;
  emptyText?: EmptyTextContent;
  errorText?: ErrorTextContent;
  searchPlaceholder?: string;
  searchDebounceMs?: number;
  autoFocusSearch?: boolean;
  initialOptions?: TItem[];
  onStateChange?: (state: ACAsyncSearchListState<TItem>) => void;
};

function resolveEmptyText(
  emptyText: EmptyTextContent,
  state: { query: string; loading: boolean; errorText: string },
) {
  return emptyText(state);
}

function resolveErrorText(
  errorText: ErrorTextContent | undefined,
  error: string,
  state: { query: string },
) {
  if (!errorText) {
    return error;
  }

  return errorText(error, state);
}

export default function ACAsyncSearchList<TItem>({
  fetchOptions,
  getOptionKey,
  renderOption,
  onSelect,
  isSelected = () => false,
  isOptionDisabled = () => false,
  emptyText = () => "No results found",
  errorText,
  searchPlaceholder = "Search",
  searchDebounceMs = 300,
  autoFocusSearch = true,
  initialOptions = [],
  onStateChange,
}: ACAsyncSearchListProps<TItem>) {
  const inputRef = useRef<InputRef>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState<TItem[]>(initialOptions);
  const [loading, setLoading] = useState(initialOptions.length === 0);
  const [error, setError] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Debounce the query changes
  const debouncedSetQuery = useMemo(
    () => debounce(setDebouncedQuery, searchDebounceMs),
    [searchDebounceMs],
  );

  // Update debounced query when query changes
  useEffect(() => {
    debouncedSetQuery(query);
    return () => debouncedSetQuery.cancel();
  }, [query, debouncedSetQuery]);

  // Effect to handle search execution based on debounced query
  useEffect(() => {
    setLoading(true);
    setError("");

    const { promise, cancel } = fetchOptions(debouncedQuery);

    promise
      .then((nextResults) => {
        setResults(nextResults);
        setLoading(false);
      })
      .catch((fetchError) => {
        setResults([]);
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : "Unable to load results.",
        );
        setLoading(false);
      });

    // Cleanup function cancels the request
    return cancel;
  }, [debouncedQuery, fetchOptions]);

  useEffect(() => {
    if (!autoFocusSearch) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [autoFocusSearch]);

  useEffect(() => {
    setHighlightedIndex(getNextSelectableIndex(-1, results, isOptionDisabled));
  }, [isOptionDisabled, results]);

  useEffect(() => {
    const highlightedItem = itemRefs.current[highlightedIndex];

    if (highlightedItem) {
      highlightedItem.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  const reload = useCallback(async () => {
    // Force a re-fetch by setting the debounced query to current query
    setDebouncedQuery(query);
  }, [query]);

  const clearQuery = useCallback(async () => {
    setQuery("");
    // The debounced effect will handle the search automatically
  }, []);

  useEffect(() => {
    onStateChange?.({
      query,
      loading,
      errorText: error,
      results,
      reload,
      clearQuery,
    });
  }, [clearQuery, error, loading, onStateChange, query, reload, results]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement | HTMLInputElement>) => {
      if (event.key === "ArrowDown") {
        setHighlightedIndex((previousIndex) =>
          getNextSelectableIndex(previousIndex, results, isOptionDisabled),
        );
        event.preventDefault();
        return;
      }

      if (event.key === "ArrowUp") {
        setHighlightedIndex((previousIndex) =>
          getPreviousSelectableIndex(previousIndex, results, isOptionDisabled),
        );
        event.preventDefault();
        return;
      }

      if (event.key === "Enter" && highlightedIndex > -1) {
        const item = results[highlightedIndex];

        if (!isOptionDisabled(item)) {
          onSelect(item);
          event.preventDefault();
        }
      }
    },
    [highlightedIndex, isOptionDisabled, onSelect, results],
  );

  return (
    <div className="ac-async-search-list" onKeyDown={handleKeyDown}>
      <div className="p-y-sm">
        <Input
          ref={inputRef}
          placeholder={searchPlaceholder}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
        />
      </div>

      <div className="overflow-y-auto" tabIndex={0} style={{ maxHeight: 320 }}>
        {loading ? (
          <div
            className="d-flex align-items-center justify-content-center p-y-sm p-x-xs text-center"
            style={{ minHeight: 72 }}
          >
            <Spin size="small" />
          </div>
        ) : error ? (
          <div
            className="d-flex align-items-center justify-content-flex-start p-y-sm p-x-xs text-center color-default"
            style={{ minHeight: 72 }}
          >
            {resolveErrorText(errorText, error, { query })}
          </div>
        ) : results.length ? (
          results.map((item, index) => {
            const disabled = isOptionDisabled(item);
            const selected = isSelected(item);
            const highlighted = index === highlightedIndex;

            return (
              <div
                key={String(getOptionKey(item))}
                id={`ac-async-search-list-item-${String(getOptionKey(item))}`}
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
                className={classNames(
                  "ac-dropdown-menu-item ac-async-search-list-item align-items-center justify-content-between gap-8",
                  {
                    "ac-dropdown-menu-item-disabled": disabled,
                    "ac-dropdown-menu-item-highlighted": highlighted,
                  },
                )}
                onMouseDown={(event) => {
                  event.stopPropagation();

                  if (!disabled) {
                    onSelect(item);
                  }
                }}
              >
                {renderOption(item, {
                  selected,
                  highlighted,
                })}
              </div>
            );
          })
        ) : (
          <div
            className="d-flex align-items-center justify-content-center p-y-sm p-x-xs text-center"
            style={{ minHeight: 72 }}
          >
            <Empty
              description={resolveEmptyText(emptyText, {
                query,
                loading,
                errorText: error,
              })}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </div>
    </div>
  );
}
